import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { 
  ChevronLeft, Calendar, Clock, Users, 
  Palmtree, XCircle, CheckCircle
} from "lucide-react";

const BookingsPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchBookings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/bookings`, { withCredentials: true });
      setBookings(response.data.bookings || []);
    } catch (error) {
      console.error("Fetch bookings error:", error);
      toast.error("Failed to load bookings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
      return;
    }
    if (user) {
      fetchBookings();
    }
  }, [authLoading, user, navigate, fetchBookings]);

  const cancelBooking = useCallback(async (bookingId) => {
    try {
      await axios.post(`${API}/bookings/${bookingId}/cancel`, {}, { withCredentials: true });
      toast.success("Booking cancelled");
      await fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel booking");
    }
  }, [fetchBookings]);

  const getStatusColor = (status) => {
    switch (status) {
      case "confirmed": return "bg-accent text-white";
      case "pending": return "bg-secondary text-secondary-foreground";
      case "cancelled": return "bg-destructive text-white";
      case "completed": return "bg-primary text-white";
      default: return "bg-muted";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "confirmed": return CheckCircle;
      case "cancelled": return XCircle;
      default: return Clock;
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="skeleton h-10 w-1/3 mb-8" />
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="skeleton h-40 rounded-2xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-background border-b">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="flex items-center gap-2">
              <ChevronLeft className="w-5 h-5" />
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Palmtree className="w-5 h-5 text-white" />
              </div>
            </Link>
            <h1 className="font-serif font-bold text-2xl">My Bookings</h1>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        {bookings.length === 0 ? (
          <div className="text-center py-20">
            <Calendar className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="font-serif text-2xl font-bold mb-2">No bookings yet</h2>
            <p className="text-muted-foreground mb-6">Start exploring and book your first experience</p>
            <Link to="/experiences">
              <Button data-testid="browse-experiences-btn">Browse Experiences</Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {bookings.map((booking) => {
              const StatusIcon = getStatusIcon(booking.status);
              return (
                <div 
                  key={booking.booking_id}
                  className="bg-white rounded-2xl shadow-card p-6"
                  data-testid={`booking-${booking.booking_id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-serif font-bold text-lg">{booking.experience_name}</h3>
                        <Badge className={getStatusColor(booking.status)}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {booking.status}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {booking.booking_date}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {booking.booking_time}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {booking.participants} {booking.participants === 1 ? "person" : "people"}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-2xl font-bold">${booking.total_price?.toFixed(2)}</p>
                        <p className="text-sm text-muted-foreground">
                          Payment: <span className="capitalize">{booking.payment_status}</span>
                        </p>
                      </div>

                      {booking.status === "pending" && (
                        <Button 
                          variant="outline" 
                          className="text-destructive border-destructive hover:bg-destructive/10"
                          onClick={() => cancelBooking(booking.booking_id)}
                          data-testid={`cancel-booking-${booking.booking_id}`}
                        >
                          Cancel
                        </Button>
                      )}
                    </div>
                  </div>

                  {booking.notes && (
                    <p className="mt-4 text-sm text-muted-foreground border-t pt-4">
                      Notes: {booking.notes}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default BookingsPage;
